import { create } from "zustand";
import {
  PDA1131_R01Dto,
  PDA1131_R01SearchParams,
  PDA1131_R01DetailDto,
  ScannedItem,
  InspectedItem,
} from "./PDA1131_R01Dto";

/** PDA1131_R01 Zustand store 타입 */
export interface PDA1131_R01Store {
  // 목록 화면 상태
  shippingOrderList: PDA1131_R01Dto[];
  loading: boolean;
  searchParams: PDA1131_R01SearchParams;

  // 상세 화면 상태
  showList: boolean;
  selectedShippingOrder: PDA1131_R01Dto | null;
  shippingDetail: PDA1131_R01DetailDto | null;

  // itemCd 스캔 상태
  itemCdInput: string;
  scannedItem: ScannedItem | null;

  // 검수 누적 (품목 단위)
  inspectedItems: InspectedItem[];

  // 목록 Actions
  setShippingOrderList: (list: PDA1131_R01Dto[]) => void;
  setLoading: (loading: boolean) => void;
  updateSearchParam: (field: keyof PDA1131_R01SearchParams, value: string) => void;

  // 상세 Actions
  setShowList: (show: boolean) => void;
  setSelectedShippingOrder: (order: PDA1131_R01Dto | null) => void;
  setShippingDetail: (detail: PDA1131_R01DetailDto | null) => void;

  // 스캔 Actions
  setItemCdInput: (value: string) => void;
  setScannedItem: (item: ScannedItem | null) => void;

  // 검수 Actions (누적/합산)
  addOrMergeInspectedItem: (item: InspectedItem) => void;
  removeInspectedItem: (itemCd: string) => void;
  clearInspectedItems: () => void;

  resetDetail: () => void;
  reset: () => void;
}

/** Date → YYYY-MM-DD (검색 기본값용) */
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** 오늘·이번 달 1일 (검색 기본 기간) */
const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

/** 검색 조건 초기값 — 당월 1일 ~ 오늘 */
const initialSearchParams: PDA1131_R01SearchParams = {
  orderNo: "",
  startDate: formatLocalDate(firstDay),
  endDate: formatLocalDate(today),
};

/** PDA1131_R01 전역 상태 store */
const usePDA1131_R01Store = create<PDA1131_R01Store>((set) => ({
  // 목록 초기값
  shippingOrderList: [],
  loading: false,
  searchParams: initialSearchParams,

  // 상세 초기값
  showList: true,
  selectedShippingOrder: null,
  shippingDetail: null,

  // 스캔 초기값
  itemCdInput: "",
  scannedItem: null,

  // 검수 초기값
  inspectedItems: [],

  // 목록 Actions
  setShippingOrderList: (list) => set({ shippingOrderList: list }),
  setLoading: (loading) => set({ loading }),
  updateSearchParam: (field, value) =>
    set((state) => ({
      searchParams: { ...state.searchParams, [field]: value },
    })),

  // 상세 Actions
  setShowList: (show) => set({ showList: show }),
  setSelectedShippingOrder: (order) => set({ selectedShippingOrder: order }),
  setShippingDetail: (detail) => set({ shippingDetail: detail }),

  // 스캔 Actions
  setItemCdInput: (value) => set({ itemCdInput: value }),
  setScannedItem: (item) => set({ scannedItem: item }),

  // 검수 Actions — 같은 itemCd면 shipQty 합산, 없으면 추가
  addOrMergeInspectedItem: (item) =>
    set((state) => {
      const idx = state.inspectedItems.findIndex((i) => i.itemCd === item.itemCd);
      if (idx >= 0) {
        const next = [...state.inspectedItems];
        next[idx] = { ...next[idx], shipQty: next[idx].shipQty + item.shipQty };
        return { inspectedItems: next };
      }
      return { inspectedItems: [...state.inspectedItems, item] };
    }),
  /** itemCd 1건 검수 대기에서 제거 */
  removeInspectedItem: (itemCd) =>
    set((state) => ({
      inspectedItems: state.inspectedItems.filter((i) => i.itemCd !== itemCd),
    })),
  clearInspectedItems: () => set({ inspectedItems: [] }),

  /** 상세 화면만 초기화 (목록·검색조건 유지) */
  resetDetail: () =>
    set({
      selectedShippingOrder: null,
      shippingDetail: null,
      itemCdInput: "",
      scannedItem: null,
      inspectedItems: [],
    }),

  /** 전체 초기화 (메뉴 이탈 시) */
  reset: () =>
    set({
      shippingOrderList: [],
      loading: false,
      searchParams: initialSearchParams,
      showList: true,
      selectedShippingOrder: null,
      shippingDetail: null,
      itemCdInput: "",
      scannedItem: null,
      inspectedItems: [],
    }),
}));

export default usePDA1131_R01Store;
